"use client";

import React, { useEffect, useState } from "react";
import { fetchAllHomeBanners } from "@/api-service/banner";
import { HomeBanner, HomeBannersResponse } from "@/packages/types/banner";
import Loader from "@/components/common/LoaderComponent";

import HeroSectionV2 from "./HeroSectionV2";
import HeroCarouselV2 from "./HeroCarouselV2";
import PromoCardSliderV2 from "./PromoCardSliderV2";

const HeroSectionV2Container: React.FC = () => {
  const [banners, setBanners] = useState<HomeBanner[]>([]);
  const [promoCards, setPromoCards] = useState<HomeBanner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadBannerData = async () => {
      try {
        const response: HomeBannersResponse = await fetchAllHomeBanners();
        if (isMounted) {
          if (response?.data) {
            setBanners(response.data.banners || []);
            setPromoCards(response.data.promoCards || []);
          } else {
            setError(true);
          }
        }
      } catch (err) {
        console.error("Failed to load hero banner data:", err);
        if (isMounted) {
          setError(true);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadBannerData();

    return () => {
      isMounted = false;
    };
  }, []);

  // Graceful fallback: if loading fails or there are no banners, hide the section entirely to avoid layout anomalies.
  if (error || (!loading && banners.length === 0)) {
    return null;
  }

  if (loading) {
    return (
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-8 my-6 flex items-center justify-center min-h-[300px] bg-slate-50/50 rounded-2xl border border-slate-100">
        <Loader variant="spinner" size="lg" label="Loading featured collections..." />
      </div>
    );
  }

  // Create Carousel Slot component
  const carouselSlot = <HeroCarouselV2 banners={banners} className="h-full w-full" />;

  // Create Promo Slot component (only if at least one promo card exists)
  const promoSlot =
    promoCards.length > 0 ? (
      <PromoCardSliderV2 promoCards={promoCards} className="h-full w-full" />
    ) : undefined;

  return <HeroSectionV2 carousel={carouselSlot} promoComponent={promoSlot} />;
};

export default HeroSectionV2Container;
